import { motion } from 'framer-motion';
import { useState, useEffect } from 'react';

// Declare global interfaces
declare global {
  interface Window {
    fs: {
      readFile: (path: string, options?: { encoding?: string }) => Promise<string | Uint8Array>;
    };
    Razorpay: any;
  }
}

interface GoshalaData {
  slNo: number;
  district: string;
  taluk: string;
  goshalaName: string;
  animalCount: number;
}

interface PaymentProps {
  amount: number;
  donorName: string;
  email: string;
  phone: string;
  type: 'donation';
  donationPurpose?: string;
  selectedGoshala?: GoshalaData;
}

// Custom hook to load goshala data from JSON file
const useGoshalaData = () => {
  const [goshalaData, setGoshalaData] = useState<GoshalaData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadGoshalaData = async () => {
      try {
        const response = await fetch('/data.json');
        const data = await response.json();
        setGoshalaData(data);
        setError(null);
      } catch (err) {
        console.error('Error loading goshala data:', err);
        setError('Failed to load goshala data');
        setGoshalaData([]);
      } finally {
        setLoading(false);
      }
    };
    loadGoshalaData();
  }, []);

  return { goshalaData, loading, error };
};

// Get unique districts from goshala data
const getUniqueDistricts = (data: GoshalaData[]): string[] => {
  return Array.from(new Set(data.map(g => g.district))).sort();
};

// PaymentGateway Component - Exact copy from original code
const PaymentGateway: React.FC<PaymentProps> = ({
  amount,
  donorName,
  email,
  phone,
  type,
  donationPurpose,
  selectedGoshala,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const RAZORPAY_KEY_ID = 'rzp_test_lXJEZJELQ8emOR';

  const loadRazorpay = (): Promise<boolean> => {
    return new Promise((resolve) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const convertNumberToWords = (num: number): string => {
    const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
    const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
    const thousands = ['', 'Thousand', 'Lakh', 'Crore'];

    if (num === 0) return 'Zero';

    const convertHundreds = (n: number): string => {
      let result = '';
      if (n >= 100) {
        result += ones[Math.floor(n / 100)] + ' Hundred ';
        n %= 100;
      }
      if (n >= 20) {
        result += tens[Math.floor(n / 10)] + ' ';
        n %= 10;
      } else if (n >= 10) {
        result += teens[n - 10] + ' ';
        return result;
      }
      if (n > 0) {
        result += ones[n] + ' ';
      }
      return result;
    };

    let result = '';
    let place = 0;
    
    while (num > 0) {
      if (num % 1000 !== 0) {
        result = convertHundreds(num % 1000) + thousands[place] + ' ' + result;
      }
      num = Math.floor(num / 1000);
      place++;
    }

    return result.trim();
  };

  const generateCertificate = (paymentId: string) => {
    try {
      const goshalaDetails = selectedGoshala ?
         `<br><br><strong>Goshala:</strong> ${selectedGoshala.goshalaName}<br><strong>Location:</strong> ${selectedGoshala.taluk}, ${selectedGoshala.district}<br><strong>Animals:</strong> ${selectedGoshala.animalCount} cows`
        : '';
        
      const certificateHTML = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="UTF-8">
          <title>GOVU RAKSHATHI RAKSHITHA Certificate</title>
          <style>
            @page { size: A4; margin: 0; }
            @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
            
            body {
              font-family: 'Times New Roman', serif;
              margin: 0;
              padding: 0;
              width: 210mm;
              height: 297mm;
              background: #d4c79a;
              position: relative;
            }
            
            .certificate-container {
              position: absolute;
              top: 30mm;
              left: 20mm;
              width: 170mm;
              height: 237mm;
              background: #f8f5e8;
              border: 4px solid #d4af37;
              border-radius: 15px;
              padding: 20mm;
              box-sizing: border-box;
              text-align: center;
            }
            
            .title { color: #8b4513; font-size: 24px; font-weight: bold; margin-bottom: 20px; }
            .recipient-name { color: #000; font-size: 22px; font-weight: bold; margin: 20px 0; }
            .amount { color: #8b4513; font-weight: bold; font-size: 14px; }
          </style>
        </head>
        <body>
          <div class="certificate-container">
            <h1 class="title">GOVU RAKSHATHI RAKSHITHA</h1>
            <div class="recipient-name">${donorName.toUpperCase()}</div>
            <div class="amount">₹${amount.toLocaleString('en-IN')}/- (${convertNumberToWords(amount)} Only)</div>
            ${goshalaDetails}
            <div>Payment ID: ${paymentId}</div>
          </div>
        </body>
        </html>
      `;

      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.write(certificateHTML);
        printWindow.document.close();
        printWindow.print();
      }
    } catch (error) {
      console.error('Error generating certificate:', error);
    }
  };

  const validateFormData = (): boolean => {
    if (!donorName.trim()) {
      setError('Donor name is required');
      return false;
    }
    if (!email.trim() || !/\S+@\S+\.\S+/.test(email)) {
      setError('Valid email is required');
      return false;
    }
    if (!phone.trim() || !/^\d{10}$/.test(phone.replace(/\D/g, ''))) {
      setError('Valid 10-digit phone number is required');
      return false;
    }
    if (amount <= 0) {
      setError('Invalid amount');
      return false;
    }
    return true;
  };

  const handlePayment = async () => {
    try {
      setIsLoading(true);
      setError(null);

      if (!validateFormData()) {
        setIsLoading(false);
        return;
      }

      const isLoaded = await loadRazorpay();
      if (!isLoaded) {
        throw new Error('Failed to load Razorpay SDK');
      }

      const options = {
        key: RAZORPAY_KEY_ID,
        amount: amount * 100,
        currency: 'INR',
        name: 'Punyakoti Dattu Yojana',
        description: donationPurpose || 'Goshala Support',
        
        handler: function (response: any) {
          setIsLoading(false);
          alert(`Payment successful! Payment ID: ${response.razorpay_payment_id}`);
          generateCertificate(response.razorpay_payment_id);
        },
        
        prefill: {
          name: donorName,
          email: email,
          contact: phone.replace(/\D/g, ''),
        },
        
        theme: {
          color: '#DC2626',
        },
        
        modal: {
          ondismiss: function() {
            setIsLoading(false);
          }
        },
        
        error: function(error: any) {
          setIsLoading(false);
          setError('Payment failed');
        }
      };

      const rzp = new (window as any).Razorpay(options);
      rzp.open();

    } catch (error: any) {
      setIsLoading(false);
      setError('Failed to initialize payment');
    }
  };

  return (
    <div className="space-y-4">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          <p className="text-sm">{error}</p>
        </div>
      )}
      
      <button
        className={`w-full bg-gradient-to-r from-red-600 to-yellow-500 text-white px-6 py-3 rounded-full font-semibold hover:shadow-lg transition-all ${
          isLoading ? 'opacity-50 cursor-not-allowed' : ''
        }`}
        onClick={handlePayment}
        disabled={isLoading}
      >
        {isLoading ? 'Processing...' : `Donate ₹${amount.toLocaleString('en-IN')} Now`}
      </button>
    </div>
  );
};

// Main Goshala Directory Component
const GoshalaDirectory: React.FC = () => {
  const { goshalaData, loading, error } = useGoshalaData();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDistrict, setSelectedDistrict] = useState<string>('');
  const [sortBy, setSortBy] = useState<'name' | 'district' | 'animals'>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedGoshala, setSelectedGoshala] = useState<GoshalaData | undefined>(undefined);
  const [paymentDetails, setPaymentDetails] = useState({
    amount: 100,
    donorName: '',
    email: '',
    phone: '',
    type: 'donation' as 'donation',
    donationPurpose: 'Goshala Support',
    selectedGoshala: undefined as GoshalaData | undefined,
  });
  const itemsPerPage = 20;

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-yellow-50 p-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              <span className="block text-red-600 mb-2">ಗೋಶಾಲೆ ನಿರ್ದೇಶಿಕೆ</span>
              Goshala Directory
            </h1>
          </div>
          <div className="flex justify-center items-center py-16">
            <div className="text-center">
              <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-red-500 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading goshala directory...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-yellow-50 p-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center py-16">
            <div className="text-red-500 text-xl mb-4">⚠️ Unable to load goshala directory</div>
            <p className="text-gray-600">Please check your connection and try again.</p>
          </div>
        </div>
      </div>
    );
  }

  // Filter and sort data
  const filteredData = goshalaData
    .filter(goshala => {
      const matchesSearch = goshala.goshalaName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           goshala.district.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           goshala.taluk.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesDistrict = !selectedDistrict || goshala.district === selectedDistrict;
      return matchesSearch && matchesDistrict;
    })
    .sort((a, b) => {
      let aValue: any, bValue: any;
      switch (sortBy) {
        case 'name':
          aValue = a.goshalaName.toLowerCase();
          bValue = b.goshalaName.toLowerCase();
          break;
        case 'district':
          aValue = a.district.toLowerCase();
          bValue = b.district.toLowerCase();
          break;
        case 'animals':
          aValue = a.animalCount;
          bValue = b.animalCount;
          break;
        default:
          return 0;
      }
      
      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

  // Pagination
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedData = filteredData.slice(startIndex, startIndex + itemsPerPage);

  const handleSort = (field: 'name' | 'district' | 'animals') => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  const getSortIcon = (field: 'name' | 'district' | 'animals') => {
    if (sortBy !== field) return '⇅';
    return sortOrder === 'asc' ? '↑' : '↓';
  };

  const handleDonateClick = (goshala: GoshalaData) => {
    setSelectedGoshala(goshala);
    setPaymentDetails(prev => ({
      ...prev,
      selectedGoshala: goshala,
      donationPurpose: `Support for ${goshala.goshalaName}`
    }));
    setShowPaymentModal(true);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPaymentDetails({ ...paymentDetails, [e.target.name]: e.target.value });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 to-yellow-50 p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            <span className="block text-red-600 mb-2">ಗೋಶಾಲೆ ನಿರ್ದೇಶಿಕೆ</span>
            Goshala Directory
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Browse through all {goshalaData.length} registered goshalas across Karnataka and support the one closest to your heart.
          </p>
        </div>

        {/* Search and Filter Controls */}
        <div className="bg-gradient-to-r from-red-50 to-yellow-50 rounded-2xl p-6 mb-8 border border-red-100">
          <div className="grid md:grid-cols-3 gap-4">
            <div className="relative">
              <input
                type="text"
                placeholder="Search goshalas, districts, or taluks..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full p-3 pl-10 border-2 border-red-200 rounded-lg focus:border-red-500 focus:outline-none bg-white"
              />
              <div className="absolute left-3 top-3 text-gray-400">🔍</div>
            </div>

            <select
              value={selectedDistrict}
              onChange={(e) => setSelectedDistrict(e.target.value)}
              className="p-3 border-2 border-red-200 rounded-lg focus:border-red-500 focus:outline-none bg-white"
            >
              <option value="">All Districts ({getUniqueDistricts(goshalaData).length})</option>
              {getUniqueDistricts(goshalaData).map((district) => (
                <option key={district} value={district}>
                  {district} ({goshalaData.filter(g => g.district === district).length})
                </option>
              ))}
            </select>

            <div className="flex items-center justify-center bg-white rounded-lg p-3 border-2 border-red-200">
              <span className="text-gray-700 font-semibold">
                {filteredData.length} of {goshalaData.length} goshalas
              </span>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
            <div className="text-center p-3 bg-white rounded-lg border border-red-100">
              <div className="text-2xl font-bold text-red-600">{filteredData.reduce((sum, g) => sum + g.animalCount, 0).toLocaleString()}</div>
              <div className="text-sm text-gray-600">Total Cows</div>
            </div>
            <div className="text-center p-3 bg-white rounded-lg border border-red-100">
              <div className="text-2xl font-bold text-red-600">{Math.round(filteredData.reduce((sum, g) => sum + g.animalCount, 0) / filteredData.length) || 0}</div>
              <div className="text-sm text-gray-600">Avg per Goshala</div>
            </div>
            <div className="text-center p-3 bg-white rounded-lg border border-red-100">
              <div className="text-2xl font-bold text-red-600">{filteredData.length > 0 ? Math.max(...filteredData.map(g => g.animalCount)).toLocaleString() : 0}</div>
              <div className="text-sm text-gray-600">Largest Goshala</div>
            </div>
            <div className="text-center p-3 bg-white rounded-lg border border-red-100">
              <div className="text-2xl font-bold text-red-600">{filteredData.filter(g => g.animalCount === 0).length}</div>
              <div className="text-sm text-gray-600">Need Support</div>
            </div>
          </div>
        </div>

        {/* Table Container */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
          <div className="bg-gradient-to-r from-red-600 to-yellow-500 text-white p-4">
            <h3 className="text-xl font-bold">Karnataka Goshalas Registry</h3>
          </div>

          <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
            <table className="w-full">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">#</th>
                  <th 
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('name')}
                  >
                    Goshala Name {getSortIcon('name')}
                  </th>
                  <th 
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('district')}
                  >
                    Location {getSortIcon('district')}
                  </th>
                  <th 
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('animals')}
                  >
                    Cows {getSortIcon('animals')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedData.map((goshala, index) => (
                  <tr key={goshala.slNo} className="hover:bg-red-50">
                    <td className="px-4 py-4 text-sm text-gray-500">{startIndex + index + 1}</td>
                    <td className="px-4 py-4 text-sm text-gray-900">
                      <div className="font-semibold">
                        {goshala.goshalaName.length > 50 
                          ? goshala.goshalaName.substring(0, 50) + '...' 
                          : goshala.goshalaName}
                      </div>
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-900">
                      <div className="font-medium text-red-600">{goshala.district}</div>
                      <div className="text-gray-500 text-xs">{goshala.taluk}</div>
                    </td>
                    <td className="px-4 py-4 text-sm">
                      <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
                        🐄 {goshala.animalCount}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-sm">
                      <button
                        className="bg-gradient-to-r from-red-500 to-yellow-500 text-white px-3 py-1 rounded-full text-xs font-semibold hover:shadow-lg"
                        onClick={() => handleDonateClick(goshala)}
                      >
                        💝 Donate Now
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="bg-gray-50 px-4 py-3 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  Showing {startIndex + 1} to {Math.min(startIndex + itemsPerPage, filteredData.length)} of {filteredData.length} results
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1 border border-gray-300 rounded text-sm hover:bg-gray-100 disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1 border border-gray-300 rounded text-sm hover:bg-gray-100 disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Payment Modal */}
        {showPaymentModal && selectedGoshala && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto relative">
              <div className="sticky top-0 bg-white rounded-t-2xl p-4 border-b border-gray-200 z-10">
                <button
                  className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 text-xl font-bold"
                  onClick={() => setShowPaymentModal(false)}
                >
                  ✕
                </button>
                
                <div className="text-center">
                  <div className="w-16 h-16 bg-gradient-to-r from-red-500 to-yellow-500 rounded-full flex items-center justify-center text-white text-2xl mx-auto mb-4">
                    🐄
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">Support This Goshala</h3>
                  <div className="bg-red-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-1">
                      {selectedGoshala.goshalaName.length > 40 
                        ? selectedGoshala.goshalaName.substring(0, 40) + '...' 
                        : selectedGoshala.goshalaName}
                    </h4>
                    <p className="text-gray-600 text-sm">{selectedGoshala.taluk}, {selectedGoshala.district}</p>
                    <p className="text-red-600 font-semibold">{selectedGoshala.animalCount} cows protected</p>
                  </div>
                </div>
              </div>

              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-gray-900 font-semibold mb-2">Name</label>
                  <input
                    type="text"
                    name="donorName"
                    value={paymentDetails.donorName}
                    onChange={handleInputChange}
                    className="w-full p-3 border-2 border-red-500 rounded-lg"
                    placeholder="Your Name"
                    required
                  />
                </div>
                <div>
                  <label className="block text-gray-900 font-semibold mb-2">Email</label>
                  <input
                    type="email"
                    name="email"
                    value={paymentDetails.email}
                    onChange={handleInputChange}
                    className="w-full p-3 border-2 border-red-500 rounded-lg"
                    placeholder="Your Email"
                    required
                  />
                </div>
                <div>
                  <label className="block text-gray-900 font-semibold mb-2">Phone</label>
                  <input
                    type="tel"
                    name="phone"
                    value={paymentDetails.phone}
                    onChange={handleInputChange}
                    className="w-full p-3 border-2 border-red-500 rounded-lg"
                    placeholder="Your Phone Number"
                    required
                  />
                </div>

                <div>
                  <label className="block text-gray-900 font-semibold mb-2">Choose Donation Amount</label>
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { amount: 70, label: 'Daily Feed' },
                      { amount: 500, label: 'Weekly Care' },
                      { amount: 2100, label: 'Monthly Care' },
                      { amount: 5000, label: 'Medical Fund' }
                    ].map((option) => (
                      <button
                        key={option.amount}
                        className={`p-3 border-2 rounded-lg transition-colors text-center ${
                          paymentDetails.amount === option.amount
                            ? 'border-red-500 bg-red-50'
                            : 'border-red-200 hover:border-red-500 hover:bg-red-50'
                        }`}
                        onClick={() => setPaymentDetails(prev => ({
                          ...prev,
                          amount: option.amount,
                          donationPurpose: `${option.label} - ${selectedGoshala.goshalaName}`
                        }))}
                      >
                        <div className="font-bold text-red-600">₹{option.amount}</div>
                        <div className="text-xs text-gray-600">{option.label}</div>
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-gray-900 font-semibold mb-2">Or Enter Custom Amount</label>
                  <input
                    type="number"
                    min="10"
                    placeholder="Enter amount (min ₹10)"
                    className="w-full p-3 border-2 border-gray-300 rounded-lg focus:border-red-500 focus:outline-none"
                    onChange={(e) => {
                      const customAmount = parseInt(e.target.value) || 0;
                      if (customAmount >= 10) {
                        setPaymentDetails(prev => ({
                          ...prev,
                          amount: customAmount,
                          donationPurpose: `Custom Donation - ${selectedGoshala.goshalaName}`
                        }));
                      }
                    }}
                  />
                </div>

                <div className="bg-red-50 p-4 rounded-lg">
                  <p className="text-gray-900 font-semibold">
                    Amount: ₹{paymentDetails.amount.toLocaleString()}
                  </p>
                  <p className="text-gray-600 text-sm">
                    Purpose: {paymentDetails.donationPurpose}
                  </p>
                </div>
              </div>

              <div className="sticky bottom-0 bg-white rounded-b-2xl p-6 border-t border-gray-200">
                <div className="flex justify-between gap-4">
                  <button
                    className="bg-gray-500 text-white px-6 py-2 rounded-full hover:bg-gray-600 transition-colors"
                    onClick={() => setShowPaymentModal(false)}
                  >
                    Cancel
                  </button>
                  <PaymentGateway {...paymentDetails} />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GoshalaDirectory;